'use client';

import React from 'react';

// Static SVG stand-in for the three.js cubes, rendered when the browser
// can't create a WebGL2 context (hardware acceleration off, GPU
// blocklisted, privacy-hardened setups). Same isometric projection as
// ResultCubeFallback so positions read consistently across the two.
// Decorative: the cube containers keep their own aria-labels, which carry
// the underlying data for assistive tech.

export interface CubeFallbackPoint {
  x: number;
  y: number;
  z: number;
  color?: string;
}

const VERTICES: [number, number, number][] = [
  [-100, -100, -100], [100, -100, -100], [100, 100, -100], [-100, 100, -100],
  [-100, -100, 100], [100, -100, 100], [100, 100, 100], [-100, 100, 100],
];

const EDGES: [number, number][] = [
  [0, 1], [1, 2], [2, 3], [3, 0],
  [4, 5], [5, 6], [6, 7], [7, 4],
  [0, 4], [1, 5], [2, 6], [3, 7],
];

function project(x: number, y: number, z: number) {
  const scale = 0.7;
  return { x: 200 + (x - z * 0.3) * scale, y: 200 - (y + z * 0.3) * scale };
}

export default function CubeStaticFallback({ points }: { points: CubeFallbackPoint[] }) {
  return (
    <svg
      viewBox="0 0 400 400"
      className="h-full w-full"
      aria-hidden="true"
      data-testid="cube-static-fallback"
    >
      {EDGES.map(([a, b], i) => {
        const p1 = project(...VERTICES[a]);
        const p2 = project(...VERTICES[b]);
        return (
          <line
            key={i}
            x1={p1.x}
            y1={p1.y}
            x2={p2.x}
            y2={p2.y}
            stroke="#d1d5db"
            strokeWidth="1.5"
          />
        );
      })}
      {points.map((p, i) => {
        const q = project(p.x, p.y, p.z);
        return (
          <circle
            key={i}
            cx={q.x}
            cy={q.y}
            r="8"
            fill={p.color ?? '#4067bf'}
            stroke="white"
            strokeWidth="2"
          />
        );
      })}
    </svg>
  );
}
